/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AssistantMCPServerRef,
  AssistantMCPServerRef$inboundSchema,
} from "./assistantmcpserverref.js";
import {
  AssistantSkillRef,
  AssistantSkillRef$inboundSchema,
} from "./assistantskillref.js";
import {
  AssistantToolsetRef,
  AssistantToolsetRef$inboundSchema,
} from "./assistanttoolsetref.js";

/**
 * NEVER_CONFIGURED when the assistant has no dedicated agent, ACTIVE when its dedicated agent is usable, and UNAVAILABLE when that agent is suspended, revoked, or deleted. This is configuration state, not permission to execute.
 */
export const IdentityState = {
  NeverConfigured: "NEVER_CONFIGURED",
  Active: "ACTIVE",
  Unavailable: "UNAVAILABLE",
} as const;
/**
 * NEVER_CONFIGURED when the assistant has no dedicated agent, ACTIVE when its dedicated agent is usable, and UNAVAILABLE when that agent is suspended, revoked, or deleted. This is configuration state, not permission to execute.
 */
export type IdentityState = ClosedEnum<typeof IdentityState>;

/**
 * The assistant status.
 */
export const AssistantStatus = {
  Active: "active",
  Paused: "paused",
} as const;
/**
 * The assistant status.
 */
export type AssistantStatus = ClosedEnum<typeof AssistantStatus>;

export type Assistant = {
  /**
   * The assistant's dedicated agent ID, when it has one.
   */
  agentId?: string | undefined;
  /**
   * Creation timestamp.
   */
  createdAt: Date;
  /**
   * The ID of the user who created the assistant, if known.
   */
  createdByUserId?: string | undefined;
  /**
   * The assistant ID.
   */
  id: string;
  /**
   * NEVER_CONFIGURED when the assistant has no dedicated agent, ACTIVE when its dedicated agent is usable, and UNAVAILABLE when that agent is suspended, revoked, or deleted. This is configuration state, not permission to execute.
   */
  identityState?: IdentityState | undefined;
  /**
   * The system instructions for the assistant.
   */
  instructions: string;
  /**
   * Maximum active warm runtimes for the assistant.
   */
  maxConcurrency: number;
  /**
   * MCP servers attached to the assistant that are not hosted (toolset-backed); hosted servers are listed under toolsets.
   */
  mcpServers: Array<AssistantMCPServerRef>;
  /**
   * The model identifier used by the assistant.
   */
  model: string;
  /**
   * The assistant name.
   */
  name: string;
  /**
   * The project ID owning the assistant.
   */
  projectId: string;
  /**
   * Skills attached to the assistant.
   */
  skills: Array<AssistantSkillRef>;
  /**
   * The assistant status.
   */
  status: AssistantStatus;
  /**
   * Hosted (toolset-backed) MCP servers attached to the assistant, by toolset slug.
   */
  toolsets: Array<AssistantToolsetRef>;
  /**
   * Last update timestamp.
   */
  updatedAt: Date;
  /**
   * Warm runtime TTL in seconds.
   */
  warmTtlSeconds: number;
};

/** @internal */
export const IdentityState$inboundSchema: z.ZodMiniEnum<typeof IdentityState> =
  z.enum(IdentityState);

/** @internal */
export const AssistantStatus$inboundSchema: z.ZodMiniEnum<
  typeof AssistantStatus
> = z.enum(AssistantStatus);

/** @internal */
export const Assistant$inboundSchema: z.ZodMiniType<Assistant, unknown> = z
  .pipe(
    z.object({
      agent_id: z.optional(z.string()),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      created_by_user_id: z.optional(z.string()),
      id: z.string(),
      identity_state: z.optional(IdentityState$inboundSchema),
      instructions: z.string(),
      max_concurrency: z.int(),
      mcp_servers: z.array(AssistantMCPServerRef$inboundSchema),
      model: z.string(),
      name: z.string(),
      project_id: z.string(),
      skills: z.array(AssistantSkillRef$inboundSchema),
      status: AssistantStatus$inboundSchema,
      toolsets: z.array(AssistantToolsetRef$inboundSchema),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      warm_ttl_seconds: z.int(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "agent_id": "agentId",
        "created_at": "createdAt",
        "created_by_user_id": "createdByUserId",
        "identity_state": "identityState",
        "max_concurrency": "maxConcurrency",
        "mcp_servers": "mcpServers",
        "project_id": "projectId",
        "updated_at": "updatedAt",
        "warm_ttl_seconds": "warmTtlSeconds",
      });
    }),
  );

export function assistantFromJSON(
  jsonString: string,
): SafeParseResult<Assistant, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Assistant$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Assistant' from JSON`,
  );
}
